import { titleCase } from "title-case";
import { analyticsReady, domReady } from "../utils/ready";
import { floorlyticsBlob } from "../utils/analytics";
import { TrafficInfo } from "./types";

/**
 * The mapping of these source values is determined on an analysis of the
 * values we have received recently. Please note that certain mapping were
 * skipped (such as yellowpages) since they are so rare in practice.
 *
 * {@link https://gist.github.com/ianks/c899971eb95de5bc8d93fa51b0908b25#file-current-lead-source-analysis-md Cukimonsta Analysis}
 */
function determineSourceVal(trafficInfo: TrafficInfo) {
  if (trafficInfo.medium === "cpc" && trafficInfo.source === "google") {
    return "Google PPC";
  } else if (trafficInfo.type === "organic") {
    return "Organic Search";
  } else if (trafficInfo.medium && trafficInfo.medium !== "none") {
    return titleCase(trafficInfo.medium);
  } else {
    return titleCase(trafficInfo.type || "");
  }
}

/**
 * Maps sourcedetail values, which is just a compatible one-to-one mapping of
 * sourcebuster's determined source
 *
 * {@link https://gist.github.com/ianks/c899971eb95de5bc8d93fa51b0908b25#file-current-lead-source-analysis-md Cukimonsta Analysis}
 */
function determineSourceDetailVal(trafficInfo: TrafficInfo) {
  return trafficInfo.source;
}

/**
 * Set a value on a bunch of inputs at one
 */
function setValueOnAllInputs(sel: string, val: string, attempts = 0) {
  const inputs = document.querySelectorAll<HTMLInputElement>(sel);

  // Try up to 10 times to load find form
  if (attempts < 10 && inputs.length === 0) {
    setTimeout(() => setValueOnAllInputs(sel, val, attempts + 1), 1000);
  } else {
    inputs.forEach((input) => (input.value = val));
  }
}

/**
 * Sets the traffic information for all Act-On form inputs on a page in a way
 * that is compatible with Cukimonsta.js
 *
 * {@link https://gist.github.com/ianks/ed96310fcc07ab146fdda063cbe97f98 Cukimonsta Information}
 */
export async function attachToFF8Form(trafficInfo: TrafficInfo): Promise<void> {
  await domReady();

  const mappedSourceVal = determineSourceVal(trafficInfo);
  const mappedSourceDetailVal = determineSourceDetailVal(trafficInfo);

  setValueOnAllInputs('input[name="source"]', mappedSourceVal);
  setValueOnAllInputs('input[name="sourcedetail"]', mappedSourceDetailVal);
}

async function syncBlobToInput(eventName: string) {
  await analyticsReady();

  analytics.on(eventName, () => {
    setTimeout(
      async () =>
        setValueOnAllInputs(
          'input[name="floorlytics-blob"]',
          await floorlyticsBlob()
        ),
      10
    );
  });
}
/**
 * Sets the traffic information for all forms which have inputs names traffic-*
 *
 * {@link https://gist.github.com/ianks/ed96310fcc07ab146fdda063cbe97f98 Cukimonstao Infomation}
 */
export async function attachToAllCompatibleForms(
  trafficInfo: TrafficInfo
): Promise<void> {
  await domReady();

  // An improved way of passing along traffic information, once we add these fields
  for (const key in trafficInfo) {
    setValueOnAllInputs(`input[name="traffic-${key}"]`, trafficInfo[key]);
    setValueOnAllInputs(`input[name="utm_${key}"]`, trafficInfo[key]);
  }

  ["page", "identify", "alias", "group"].forEach((eventName) => {
    syncBlobToInput(eventName);
  });

  setValueOnAllInputs(
    'input[name="floorlytics-blob"]',
    await floorlyticsBlob()
  );
}
